import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, LayoutDashboard, Play, Calendar, Heart, Phone, MoreHorizontal, User,
  QrCode, ClipboardList, Settings, GraduationCap, CreditCard, MessageCircle,
  Users, Contact, BarChart3, UserCircle, FileBarChart, Map, Network,
  ListChecks, Activity, Route, Target, AlertTriangle
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import MoreMenu from './MoreMenu';
import { markTap } from '../../utils/tabPerf';
import { preloadHandlers } from '../../utils/routePreload';
import usePendingCount from '../../hooks/usePendingCount';

/**
 * Universal bottom navigation — same 5-tab structure for every role:
 * 1. Accueil      → public home (/)
 * 2. Mon Espace   → role dashboard (hidden for visitors)
 * 3. Shortcut     → one role-specific quick link
 * 4. Profil       → settings/profile (hidden for visitors)
 * 5. Plus         → overflow menu (always last)
 */

// ─── Role configurations ────────────────────────────────────────────

const ROLE_NAV_CONFIG = {
  visitor: {
    monEspace: null,
    shortcut: { id: 'events', label: 'Événements', icon: Calendar, path: '/events' },
    moreItems: [
      { id: 'watch', label: 'Regarder', icon: Play, path: '/watch' },
      { id: 'contact', label: 'Contact', icon: Phone, path: '/contact' },
      { id: 'giving', label: 'Donner', icon: Heart, path: '/giving' },
    ],
    profilePath: null,
  },
  // ── AUTHENTICATED ROLES ───────────────────────────────────────────
  // Design decision (2026-04-21): Accueil is ALWAYS the public "/" HomeScreen
  // for every role. Mon Espace is the role-specific dashboard. This removes
  // the long-standing URL collision where Accueil and Mon Espace pointed to
  // the same URL for authenticated users — which caused the recurring
  // "Accueil does nothing" bug: indicator stuck on Mon Espace, tap-to-current
  // URL was a silent no-op, user couldn't tell the tab did anything.
  // Accueil = community/church feed (videos, events, Bible plans).
  // Mon Espace = personal role dashboard.
  member: {
    accueilPath: '/',
    monEspace: { path: '/member/home', altPaths: ['/member/dashboard'] },
    shortcut: { id: 'carte', label: 'Ma Carte', icon: QrCode, path: '/member/carte' },
    extraTab: { id: 'calendrier', label: 'Calendrier', icon: Calendar, path: '/member/calendar', altPaths: ['/member/appointments', '/events'] },
    moreItems: [],
    profilePath: '/member/settings',
    profileAltPaths: ['/member/profile'],
  },
  leader: {
    // 5 flat tabs, no Plus menu
    // Accueil · Mon Espace · Ma Carte · Mes RDV · Profil
    accueilPath: '/',
    monEspace: { path: '/leader/dashboard', altPaths: ['/leader/home'] },
    shortcut: { id: 'carte', label: 'Ma Carte', icon: QrCode, path: '/member/carte' },
    extraTab: { id: 'mes-rdv', label: 'Mes RDV', icon: Calendar, path: '/leader/rdv', altPaths: ['/leader/availability'] },
    moreItems: [],
    profilePath: '/leader/settings',
    // /member/profile is the shared AuthenticatedRoute profile screen —
    // if a leader ever lands there, the Profil tab must still light up.
    profileAltPaths: ['/leader/settings', '/member/profile', '/member/settings'],
  },
  dg: {
    // 5 flat tabs, no Plus menu
    // Accueil · Mon Espace · Alertes · Équipe · Profil
    accueilPath: '/',
    monEspace: { path: '/dg/dashboard', altPaths: ['/dg'] },
    shortcut: { id: 'alertes', label: 'Alertes', icon: AlertTriangle, path: '/dg/alerts' },
    extraTab: { id: 'equipe', label: 'Équipe', icon: Users, path: '/dg/team', altPaths: ['/dg/leaders'] },
    moreItems: [],
    profilePath: '/dg/settings',
    profileAltPaths: ['/member/profile', '/member/settings'],
  },
  admin: {
    // admin shares the DG navbar
    accueilPath: '/',
    monEspace: { path: '/dg/dashboard', altPaths: ['/dg'] },
    shortcut: { id: 'alertes', label: 'Alertes', icon: AlertTriangle, path: '/dg/alerts' },
    extraTab: { id: 'equipe', label: 'Équipe', icon: Users, path: '/dg/team', altPaths: ['/dg/leaders'] },
    moreItems: [],
    profilePath: '/dg/settings',
    profileAltPaths: ['/member/profile', '/member/settings'],
  },
};

// ─── Component ──────────────────────────────────────────────────────

const UniversalBottomNav = ({ role = 'visitor' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [moreOpen, setMoreOpen] = useState(false);

  const config = ROLE_NAV_CONFIG[role] || ROLE_NAV_CONFIG.visitor;
  const isVisitor = !config.monEspace;
  // Refonte 2026 : seul le rôle MEMBRE reçoit le bouton central « Ma carte »
  // surélevé (décision Louis). Les autres rôles gardent la barre plate — aucun
  // impact hors membre.
  const isMember = role === 'member';

  // Pending-count badge. The hook polls /api/appointments/pending-count
  // every 30s (paused while document.hidden) and returns 0 for roles
  // whose backend always returns 0. We route it to:
  //   - leader  → Mes RDV (extraTab)
  //   - dg/admin → Alertes (shortcut)
  // Other tabs render no badge.
  const pendingCount = usePendingCount();
  const shortcutCount = (role === 'dg' || role === 'admin') ? pendingCount : 0;
  const extraTabCount = role === 'leader' ? pendingCount : 0;

  // HARDCODED 5-TAB ROLES — these roles use [Accueil · Mon Espace · Shortcut ·
  // ExtraTab · Profil] with zero Plus menu. We strip moreItems defensively so
  // a stale config or third-party tweak can never flip them back to Plus.
  const FIVE_TAB_ROLES = new Set(['member', 'leader', 'dg', 'admin']);
  // eslint-disable-next-line no-unused-vars
  let hasPermissionFn = null;
  try {
    const auth = useAuth();
    hasPermissionFn = auth.hasPermission;
  } catch (e) { /* outside AuthProvider */ }

  // Never add items to moreItems for 5-tab roles — no Plus menu under any
  // circumstance (not even if a permission path in a future patch tries to).
  // Memoize so the array reference is stable per role — without this the
  // resolver useCallback churns every render and re-fires the useEffect.
  const effectiveMoreItems = useMemo(
    () => (FIVE_TAB_ROLES.has(role) ? [] : [...(config.moreItems || [])]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role]
  );
  const hasMoreMenu = effectiveMoreItems.length > 0;
  const accueilPath = config.accueilPath || '/';

  // ── Path → tab-key resolver ──
  // Returns a single tab key for a given URL. Source of truth when the
  // location changes from outside this component (back/forward, programmatic
  // nav from other screens). Order matters: more specific tabs win over
  // Accueil when paths overlap (in practice, after the Accueil="/" split,
  // overlaps are rare — but the precedence is still defensive).
  const resolveTabFromPath = useCallback((pathname) => {
    const exact = (path, altPaths = [], allowPrefix = true) => {
      if (pathname === path) return true;
      if (altPaths && altPaths.some(p => pathname === p)) return true;
      if (allowPrefix && pathname.startsWith(path + '/')) return true;
      return false;
    };
    // Mon Espace
    if (config.monEspace) {
      const me = config.monEspace;
      if (exact(me.path, me.altPaths, !me.exactMatch)) return 'monEspace';
    }
    // Shortcut
    if (exact(config.shortcut.path)) return 'shortcut';
    // Extra tab
    if (config.extraTab && exact(config.extraTab.path, config.extraTab.altPaths)) {
      return 'extraTab';
    }
    // Profile
    if (config.profilePath && exact(config.profilePath, config.profileAltPaths)) {
      return 'profil';
    }
    // Plus (visitor moreItems)
    if (hasMoreMenu && effectiveMoreItems.some(item =>
      pathname === item.path || pathname.startsWith(item.path + '/')
    )) return 'plus';
    // Accueil — exact-match only ('/'), checked last so role-specific tabs win
    if (pathname === accueilPath) return 'accueil';
    return null;
  }, [config, hasMoreMenu, effectiveMoreItems, accueilPath]);

  // ── Optimistic active-tab state ──
  // Initialize from current URL so the first paint matches reality. On every
  // tap, set this BEFORE navigate() so the indicator moves on the next paint
  // without waiting for React Router to commit the route change. The effect
  // below re-syncs from the URL when navigation lands (or when location
  // changes from elsewhere) so we can never drift from reality.
  const [activeTabKey, setActiveTabKey] = useState(() => resolveTabFromPath(location.pathname));

  useEffect(() => {
    const next = resolveTabFromPath(location.pathname);
    setActiveTabKey(prev => (prev === next ? prev : next));
  }, [location.pathname, resolveTabFromPath]);

  // Tap handler — optimistic indicator update + navigation in parallel.
  // If the user is already on the destination, scroll-to-top instead of a
  // no-op navigate() (preserves the "Accueil does something" fix from 923af9ca).
  const handleTabTap = (tabKey, path) => {
    setActiveTabKey(tabKey);
    if (location.pathname === path) {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
    } else {
      markTap(path);
      navigate(path);
    }
  };

  // Derive each tab's active flag from the optimistic state. These are the
  // same boolean shape the TabButton expects, just sourced from a single
  // state value instead of five independent path-derived computations.
  const accueilActive = activeTabKey === 'accueil';
  const monEspaceActive = activeTabKey === 'monEspace';
  const shortcutActive = activeTabKey === 'shortcut';
  const extraTabActive = activeTabKey === 'extraTab';
  const profileActive = activeTabKey === 'profil';
  const isMoreActive = activeTabKey === 'plus';

  // ── Shared styles ──
  const activeColor = 'text-[#9398DE]';
  const inactiveColor = isDark
    ? 'text-[#6A6A6A] hover:text-[#9A9A9A]'
    : 'text-gray-400 hover:text-gray-600';

  // Indicator bar — rendered ALWAYS so the opacity transition can run when
  // the active tab changes. Conditional render (`{active && bar}`) skipped
  // the transition because the element wasn't in the DOM in the inactive
  // state. duration-150 ease-out matches iOS-style tab UX.
  const TabButton = ({ id, label, icon: Icon, active, onClick, count = 0, preloadPath }) => (
    <button
      onClick={onClick}
      {...(preloadPath ? preloadHandlers(preloadPath) : {})}
      data-testid={`nav-${id}`}
      className={`
        flex flex-col items-center justify-center flex-1 min-h-[44px] min-w-[44px]
        transition-colors duration-150 ease-out relative
        ${active ? activeColor : inactiveColor}
      `}
    >
      <div
        aria-hidden
        className={`absolute -top-1 left-1/2 -translate-x-1/2 w-8 h-1 bg-[#9398DE] rounded-b-full transition-opacity duration-150 ease-out ${active ? 'opacity-100' : 'opacity-0'}`}
      />
      <div className="relative">
        <Icon size={22} className="mb-0.5" strokeWidth={active ? 2.5 : 2} />
        {count > 0 && (
          <span
            data-testid={`nav-${id}-badge`}
            aria-label={`${count} en attente`}
            className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-[18px] text-center shadow-sm ring-2 ring-white dark:ring-[#2D2D2D]"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </div>
      <span className={`text-[10px] tracking-wide whitespace-nowrap ${active ? 'font-semibold' : 'font-medium'}`}>
        {label}
      </span>
    </button>
  );

  const ShortcutIcon = config.shortcut.icon;

  return (
    <>
      <nav
        className={`pcc-bottom-nav border-t transition-colors duration-200 ${
          isDark
            ? 'bg-[#2D2D2D] border-[#444444]'
            : 'bg-white border-gray-100'
        }`}
        style={{
          // Bulletproof anchor — inline to beat any Tailwind precedence edge case
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          zIndex: 9999,
          boxShadow: isDark
            ? '0 -4px 20px rgba(0, 0, 0, 0.3)'
            : '0 -4px 20px rgba(0, 0, 0, 0.08)',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)',
        }}
        data-testid="universal-bottom-nav"
      >
        <div className="flex justify-around items-center h-16 max-w-lg lg:max-w-none mx-auto pt-1">
          {/* Tab 1: Accueil — role-aware home (see accueilPath in config) */}
          <TabButton
            id="accueil"
            label="Accueil"
            icon={Home}
            active={accueilActive}
            onClick={() => handleTabTap('accueil', accueilPath)}
            preloadPath={accueilPath}
          />

          {/* Tab 2: Mon Espace — role dashboard (hidden for visitors) */}
          {!isVisitor && (
            <TabButton
              id="mon-espace"
              label="Mon Espace"
              icon={LayoutDashboard}
              active={monEspaceActive}
              onClick={() => handleTabTap('monEspace', config.monEspace.path)}
              preloadPath={config.monEspace.path}
            />
          )}

          {/* Tab 3: Role shortcut — membre = bouton central surélevé */}
          {isMember ? (
            <button
              onClick={() => handleTabTap('shortcut', config.shortcut.path)}
              {...preloadHandlers(config.shortcut.path)}
              data-testid={`nav-${config.shortcut.id}`}
              aria-label={config.shortcut.label}
              className="flex flex-col items-center justify-center flex-1 min-w-[44px] relative"
            >
              <span
                className="flex items-center justify-center active:scale-95 transition-transform"
                style={{
                  width: 52, height: 52, borderRadius: 18, marginTop: -22,
                  background: 'linear-gradient(135deg, #9398DE 0%, #7B80C9 100%)',
                  boxShadow: '0 6px 16px rgba(147, 152, 222, 0.5)',
                }}
              >
                <ShortcutIcon size={26} color="#ffffff" strokeWidth={2.4} />
              </span>
              <span className={`text-[10px] tracking-wide whitespace-nowrap mt-1 ${shortcutActive ? 'font-semibold ' + activeColor : inactiveColor}`}>
                {config.shortcut.label}
              </span>
            </button>
          ) : (
            <TabButton
              id={config.shortcut.id}
              label={config.shortcut.label}
              icon={ShortcutIcon}
              active={shortcutActive}
              count={shortcutCount}
              onClick={() => handleTabTap('shortcut', config.shortcut.path)}
              preloadPath={config.shortcut.path}
            />
          )}

          {/* Tab 4: Extra tab (e.g. Calendrier) OR Profil */}
          {config.extraTab && !hasMoreMenu ? (
            <TabButton
              id={config.extraTab.id}
              label={config.extraTab.label}
              icon={config.extraTab.icon}
              active={extraTabActive}
              count={extraTabCount}
              onClick={() => handleTabTap('extraTab', config.extraTab.path)}
              preloadPath={config.extraTab.path}
            />
          ) : !isVisitor ? (
            <TabButton
              id="profil"
              label="Profil"
              icon={User}
              active={profileActive}
              onClick={() => handleTabTap('profil', config.profilePath)}
            />
          ) : null}

          {/* Tab 5: Profil (when extraTab used) OR Plus */}
          {config.extraTab && !hasMoreMenu ? (
            !isVisitor && (
              <TabButton
                id="profil"
                label="Profil"
                icon={User}
                active={profileActive}
                onClick={() => handleTabTap('profil', config.profilePath)}
                preloadPath={config.profilePath}
              />
            )
          ) : (
            <TabButton
              id="plus"
              label="Plus"
              icon={MoreHorizontal}
              active={isMoreActive}
              onClick={() => setMoreOpen(true)}
            />
          )}
        </div>
      </nav>

      {hasMoreMenu && <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} items={effectiveMoreItems} />}
    </>
  );
};

export default UniversalBottomNav;
